import { useState, useEffect, useMemo } from 'react';
import { SlideDrawer } from '@/components/ui/slide-drawer';
import { CheckCircle2, ExternalLink, KeyRound, Loader2, Shield, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { useCapabilityCreate } from '@/hooks/use-entity-mutations';
import { logger } from '@/lib/telemetry';
import { oauthStrategyFromTypeConfig, startOAuthAuthorizationCode } from '@/lib/oauth-connect';
import { capabilityDetailPath } from '@/types/capability-types';
import type { IntegrationAuth, AuthType, AuthStrategyConfig } from '@/types/integration-form-types';

interface StrategyField {
    name: string;
    label: string;
    type: 'text' | 'password' | 'url' | string;
    required: boolean;
    placeholder?: string;
}

interface ConnectionSetupDrawerProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    integrationName: string;
    appKey?: string;
    integrationRef?: string;
    auth?: IntegrationAuth;
    typeConfig?: Record<string, unknown>;
}

const AUTH_TYPE_LABELS: Record<string, string> = {
    oauth2_authorization_code: 'OAuth2 (Authorization Code)',
    oauth2_client_credentials: 'OAuth2 (Client Credentials)',
    api_key: 'API Key',
    basic_auth: 'Basic Authentication',
    bearer_token: 'Bearer Token',
    hmac: 'HMAC Signing',
    aws_sigv4: 'AWS Signature V4',
    custom_header: 'Custom Header',
};

const AUTH_TYPE_ICONS: Record<string, typeof KeyRound> = {
    oauth2_authorization_code: ExternalLink,
    oauth2_client_credentials: Shield,
    api_key: KeyRound,
    basic_auth: KeyRound,
    bearer_token: KeyRound,
    hmac: Shield,
    aws_sigv4: Shield,
    custom_header: KeyRound,
};

const FALLBACK_FIELDS: Record<string, StrategyField[]> = {
    api_key: [{ name: 'api_key', label: 'API Key', type: 'password', required: true }],
    bearer_token: [{ name: 'access_token', label: 'Access Token', type: 'password', required: true }],
    basic_auth: [
        { name: 'username', label: 'Username', type: 'text', required: true },
        { name: 'password', label: 'Password', type: 'password', required: true },
    ],
    hmac: [{ name: 'secret', label: 'HMAC Secret', type: 'password', required: true }],
    aws_sigv4: [
        { name: 'access_key_id', label: 'Access Key ID', type: 'text', required: true },
        { name: 'secret_access_key', label: 'Secret Access Key', type: 'password', required: true },
        { name: 'region', label: 'Region', type: 'text', required: true },
    ],
    custom_header: [
        { name: 'header_name', label: 'Header Name', type: 'text', required: true },
        { name: 'header_value', label: 'Header Value', type: 'password', required: true },
    ],
};

type WizardStep = 'select_auth' | 'credentials' | 'success';

function getStrategyFields(auth: IntegrationAuth | undefined, authType: string): StrategyField[] {
    if (!auth?.strategies) return FALLBACK_FIELDS[authType] ?? [];
    const strategy = auth.strategies[authType as AuthType] as (AuthStrategyConfig & { fields?: StrategyField[] }) | undefined;
    if (strategy?.fields && Array.isArray(strategy.fields) && strategy.fields.length > 0) {
        return strategy.fields;
    }
    return FALLBACK_FIELDS[authType] ?? [];
}

export function ConnectionSetupDrawer({
    open,
    onOpenChange,
    integrationName,
    appKey,
    integrationRef,
    auth,
    typeConfig,
}: ConnectionSetupDrawerProps) {
    const createCapability = useCapabilityCreate(appKey ?? '__placeholder__');
    const supportedTypes = auth?.supported_types;
    const typeCount = supportedTypes?.length ?? 0;
    const singleAuthType = typeCount === 1;

    const [step, setStep] = useState<WizardStep>('select_auth');
    const [selectedAuthType, setSelectedAuthType] = useState('');
    const [credentials, setCredentials] = useState<Record<string, string>>({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (open) {
            if (singleAuthType && supportedTypes) {
                setSelectedAuthType(supportedTypes[0]);
                setStep('credentials');
            } else {
                setSelectedAuthType('');
                setStep('select_auth');
            }
            setCredentials({});
            setIsSubmitting(false);
        }
    }, [open, singleAuthType, supportedTypes]);

    const fields = useMemo(
        () => getStrategyFields(auth, selectedAuthType),
        [auth, selectedAuthType],
    );

    const oauthStrategy = useMemo(() => oauthStrategyFromTypeConfig(typeConfig), [typeConfig]);

    const isOAuth2Redirect = selectedAuthType === 'oauth2_authorization_code';

    const allRequiredFilled = useMemo(() => {
        if (isOAuth2Redirect) {
            if (oauthStrategy.registrationEndpoint) return true;
            return (credentials.client_id ?? '').trim().length > 0;
        }
        return fields
            .filter((f) => f.required)
            .every((f) => (credentials[f.name] ?? '').trim().length > 0);
    }, [fields, credentials, isOAuth2Redirect, oauthStrategy.registrationEndpoint]);

    const handleReset = () => {
        setStep('select_auth');
        setSelectedAuthType('');
        setCredentials({});
        setIsSubmitting(false);
    };

    const handleClose = () => {
        if (isSubmitting) return;
        handleReset();
        onOpenChange(false);
    };

    const handleAuthTypeSelect = (authType: string) => {
        setSelectedAuthType(authType);
        setCredentials({});
        setStep('credentials');
    };

    const handleSubmit = async () => {
        setIsSubmitting(true);
        try {
            if (appKey && integrationRef) {
                const slug = integrationName
                    .toLowerCase()
                    .replace(/[^a-z0-9\s-]/g, '')
                    .replace(/\s+/g, '-')
                    .replace(/-+/g, '-')
                    .replace(/^-|-$/g, '');

                const connectionTypeConfig = {
                    integration_ref: integrationRef,
                    auth_type: selectedAuthType,
                    status: selectedAuthType === 'oauth2_authorization_code' ? 'pending' : 'active',
                    owner: { tenant_id: appKey },
                    credentials,
                    metadata: { connected_at: new Date().toISOString() },
                };

                const payload = {
                    name: `connection.${slug}-${Date.now().toString(36)}`,
                    display_name: `${integrationName} Connection`,
                    type: 'connection',
                    description: `Connection to ${integrationName} via ${AUTH_TYPE_LABELS[selectedAuthType] ?? selectedAuthType}`,
                    tags: ['connection', selectedAuthType],
                    type_config: connectionTypeConfig,
                };

                logger.info('ConnectionSetupDrawer:handleSubmit', { name: payload.name });
                const created = await createCapability.mutateAsync(payload);
                const createdName = created && typeof created === 'object' && 'name' in created
                    ? String((created as { name: string }).name)
                    : payload.name;

                                if (selectedAuthType === 'oauth2_authorization_code') {
                    const oauth = oauthStrategy;
                    if (!oauth.authUrl) {
                        throw new Error('This integration has no authorization URL. Discover the MCP server or set it on the Authorization tab.');
                    }
                    toast.success(`Connection created. Redirecting to sign in…`);
                    await startOAuthAuthorizationCode({
                        connection_name: createdName,
                        app_key: appKey,
                        integration_ref: integrationRef,
                        auth_url: oauth.authUrl,
                        token_url: oauth.tokenUrl,
                        client_id: credentials.client_id,
                        client_secret: credentials.client_secret,
                        scopes: oauth.scopes,
                        pkce: oauth.pkce,
                        registration_endpoint: oauth.registrationEndpoint,
                        resource: oauth.resource,
                        return_to: capabilityDetailPath(appKey, 'connection', createdName),
                    });
                    return;
                }
            }

            setStep('success');
            toast.success(`Connected to ${integrationName}`);
        } catch (err) {
            logger.error('ConnectionSetupDrawer:handleSubmit', { error: err });
            const msg = err instanceof Error ? err.message : 'Failed to create connection';
            toast.error(msg);
        } finally {
            setIsSubmitting(false);
        }
    };

    const renderAuthTypeSelection = () => {
        if (typeCount > 0 && supportedTypes) {
            return (
                <div className="space-y-3">
                    {supportedTypes.map((authType) => {
                        const Icon = AUTH_TYPE_ICONS[authType] ?? KeyRound;
                        return (
                            <button
                                key={authType}
                                onClick={() => handleAuthTypeSelect(authType)}
                                className="w-full flex items-center gap-3 p-3 rounded-lg border hover:border-primary/50 hover:bg-accent transition-colors text-left"
                            >
                                <div className="h-9 w-9 rounded-md bg-primary/10 flex items-center justify-center shrink-0">
                                    <Icon className="h-4 w-4 text-primary" />
                                </div>
                                <div>
                                    <p className="text-sm font-medium">
                                        {AUTH_TYPE_LABELS[authType] ?? authType}
                                    </p>
                                    <p className="text-xs text-muted-foreground">
                                        {authType.startsWith('oauth2')
                                            ? 'Authorize via provider redirect'
                                            : 'Enter credentials directly'}
                                    </p>
                                </div>
                            </button>
                        );
                    })}
                </div>
            );
        }

        return (
            <div className="space-y-3">
                <Select value={selectedAuthType} onValueChange={handleAuthTypeSelect}>
                    <SelectTrigger>
                        <SelectValue placeholder="Select authentication method" />
                    </SelectTrigger>
                    <SelectContent>
                        {Object.entries(AUTH_TYPE_LABELS).map(([value, label]) => (
                            <SelectItem key={value} value={value}>{label}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>
        );
    };

    const renderCredentialsForm = () => {
        if (isOAuth2Redirect) {
            return (
                <div className="space-y-4">
                    <div className="rounded-lg bg-muted/50 p-6 text-center">
                        <ExternalLink className="h-8 w-8 text-primary mx-auto mb-2" />
                        <p className="text-sm font-medium">OAuth2 Authorization</p>
                        <p className="text-xs text-muted-foreground mt-1">
                            You will be redirected to the provider login page to authorize access.
                        </p>
                    </div>
                    <div>
                        <Label htmlFor="conn-client-id">
                            Client ID
                            {!oauthStrategy.registrationEndpoint && (
                                <span className="text-destructive ml-0.5">*</span>
                            )}
                        </Label>
                        <Input
                            id="conn-client-id"
                            placeholder={oauthStrategy.registrationEndpoint
                                ? 'Optional — registered automatically if empty'
                                : 'OAuth client ID'}
                            value={credentials.client_id ?? ''}
                            onChange={(e) => setCredentials({ ...credentials, client_id: e.target.value })}
                        />
                    </div>
                    <div className="flex items-center justify-end gap-2 pt-2">
                        {!singleAuthType && (
                            <Button variant="outline" onClick={() => { setStep('select_auth'); setSelectedAuthType(''); }}>
                                Back
                            </Button>
                        )}
                        <Button onClick={handleSubmit} disabled={isSubmitting || !allRequiredFilled}>
                            {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                            Connect with OAuth2
                        </Button>
                    </div>
                </div>
            );
        }

        return (
            <div className="space-y-4">
                {fields.map((field) => (
                    <div key={field.name}>
                        <Label htmlFor={`conn-${field.name}`}>
                            {field.label}
                            {field.required && <span className="text-destructive ml-0.5">*</span>}
                        </Label>
                        <Input
                            id={`conn-${field.name}`}
                            type={field.type === 'password' ? 'password' : 'text'}
                            placeholder={field.placeholder ?? `Enter ${field.label.toLowerCase()}...`}
                            value={credentials[field.name] ?? ''}
                            onChange={(e) => setCredentials({ ...credentials, [field.name]: e.target.value })}
                        />
                    </div>
                ))}
                <div className="flex items-center justify-end gap-2 pt-2">
                    {!singleAuthType && (
                        <Button variant="outline" onClick={() => { setStep('select_auth'); setSelectedAuthType(''); }}>
                            Back
                        </Button>
                    )}
                    <Button onClick={handleSubmit} disabled={isSubmitting || !allRequiredFilled}>
                        {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                        Connect
                    </Button>
                </div>
            </div>
        );
    };

    const renderSuccess = () => (
        <div className="text-center py-6">
            <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-3" />
            <p className="text-lg font-medium">Connected!</p>
            <p className="text-sm text-muted-foreground mt-1 mb-4">
                Your connection to {integrationName} has been established.
            </p>
            <Badge variant="secondary">{AUTH_TYPE_LABELS[selectedAuthType] ?? selectedAuthType}</Badge>
            <div className="mt-6">
                <Button onClick={handleClose}>Done</Button>
            </div>
        </div>
    );

    const stepTitle =
        step === 'success'
            ? 'Connection Established'
            : `Connect to ${integrationName}`;

    const stepDescription =
        step === 'select_auth'
            ? 'Choose how you want to authenticate.'
            : step === 'credentials'
              ? 'Enter your credentials to establish the connection.'
              : undefined;

    return (
        <SlideDrawer isOpen={open} onClose={handleClose}>
                        <div className="border-b border-slate-100 px-6 pb-4 pt-2">
                            <div className="flex items-center justify-between">
                                <div>
                                    <h2 className="text-lg font-semibold text-slate-900">
                                        {stepTitle}
                                    </h2>
                                    {stepDescription && (
                                        <p className="text-sm text-slate-500 mt-0.5">
                                            {stepDescription}
                                        </p>
                                    )}
                                </div>
                                <button
                                    type="button"
                                    onClick={handleClose}
                                    disabled={isSubmitting}
                                    className="rounded-full p-2 text-slate-400 outline-none transition-colors hover:bg-slate-200 hover:text-slate-600 focus:ring-2 focus:ring-slate-900 disabled:opacity-50"
                                    aria-label="Close"
                                >
                                    <X size={18} />
                                </button>
                            </div>
                        </div>

                        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
                            {step === 'select_auth' && renderAuthTypeSelection()}
                            {step === 'credentials' && renderCredentialsForm()}
                            {step === 'success' && renderSuccess()}
                        </div>
        </SlideDrawer>
    );
}
